import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Package, ArrowRight, Sparkles, Droplets, Shield, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const routines = [
  {
    type: "Rutina para Piel Grasa",
    description: "Controla el brillo excesivo sin resecar tu piel. Ingredientes clave: BHA, niacinamida, te verde.",
    steps: [
      "Limpiador en gel con BHA (controla grasa y limpia poros)",
      "Toner AHA/BHA/PHA (exfolia y equilibra)",
      "Serum ligero con niacinamida (controla sebo e ilumina)",
      "Hidratante oil-free en gel (hidrata sin dejar grasa)",
      "Protector solar matificante SPF 50+ (protege sin brillo)"
    ],
    color: "var(--sage)",
    icon: Droplets,
    recommended: ["SOME BY MI Miracle Toner", "PURITO Sunscreen"]
  },
  {
    type: "Rutina para Piel Seca",
    description: "Hidratacion intensa en capas para una piel suave y nutrida. Ingredientes clave: ceramidas, acido hialuronico, manteca de karite.",
    steps: [
      "Aceite limpiador (remueve maquillaje sin resecar)",
      "Limpiador cremoso suave (limpia respetando la barrera)",
      "Toner hidratante con acido hialuronico (primera capa de hidratacion)",
      "Esencia nutritiva (penetra profundamente)",
      "Serum con ceramidas (refuerza la barrera cutanea)",
      "Crema rica nutritiva (sella toda la hidratacion)",
      "Protector solar hidratante (protege y nutre)"
    ],
    color: "var(--violet)",
    icon: Heart,
    recommended: ["COSRX Snail Mucin Essence", "Innisfree Green Tea Serum"]
  },
  {
    type: "Rutina para Piel Sensible",
    description: "Calma, protege y repara la piel reactiva. Ingredientes clave: centella asiatica, madecassoside, aloe vera.",
    steps: [
      "Limpiador suave pH bajo (no irrita la piel)",
      "Toner con centella asiatica (calma y repara)",
      "Serum calmante con madecassoside (reduce enrojecimiento)",
      "Crema barrera con ceramidas (protege contra irritantes)",
      "Protector solar mineral suave (proteccion sin quimicos)"
    ],
    color: "var(--sage-dark)",
    icon: Shield,
    recommended: ["SKIN1004 Centella Kit", "Beauty of Joseon Serum"]
  },
];

export default function KitsPage() {
  const [kits, setKits] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products?category=kits`).then(r => setKits(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="kits-page">
      {/* Header */}
      <div className="bg-[var(--sage-light)] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-[var(--sage)] text-[var(--dark)] border-0 mb-4 text-xs">Segun tu tipo de piel</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--dark)] mb-4">Kits y Rutinas</h1>
          <p className="text-base lg:text-lg text-[var(--dark)]/60 max-w-2xl mx-auto">
            Kits completos de skincare coreano y rutinas personalizadas. Encuentra la combinacion perfecta para transformar tu piel.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Kits */}
        {kits.length > 0 && (
          <div className="mb-20">
            <h2 className="text-2xl sm:text-3xl font-bold text-[var(--dark)] mb-8 flex items-center gap-3">
              <Package className="h-7 w-7 text-[var(--violet)]" /> Kits Recomendados
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {kits.map(kit => (
                <Link key={kit.id} to={`/producto/${kit.id}`} className="group bg-white rounded-2xl overflow-hidden border border-[var(--sage)]/20 hover:shadow-lg transition-all">
                  <div className="aspect-video overflow-hidden bg-[var(--cream)]">
                    <img src={kit.images?.[0]} alt={kit.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5 space-y-3">
                    <Badge className="bg-[var(--violet-light)] text-[var(--violet)] border-0 text-xs">{kit.brand}</Badge>
                    <h3 className="text-base font-bold text-[var(--dark)]">{kit.name}</h3>
                    <p className="text-sm text-[var(--dark)]/50 line-clamp-2">{kit.description}</p>
                    <div className="flex items-baseline gap-2">
                      {kit.discount_price ? (
                        <><span className="text-xl font-bold text-[var(--violet)]">${kit.discount_price.toFixed(2)}</span><span className="text-sm text-[var(--dark)]/30 line-through">${kit.price.toFixed(2)}</span></>
                      ) : (
                        <span className="text-xl font-bold text-[var(--dark)]">${kit.price.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Routines */}
        <h2 className="text-2xl sm:text-3xl font-bold text-[var(--dark)] mb-3 flex items-center gap-3">
          <Sparkles className="h-7 w-7 text-[var(--sage-dark)]" /> Rutinas Recomendadas por Tipo de Piel
        </h2>
        <p className="text-base text-[var(--dark)]/50 mb-8">Cada rutina esta disenada con productos coreanos especificos para tu tipo de piel.</p>

        <div className="space-y-8">
          {routines.map((r, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 lg:p-8 border border-[var(--sage)]/20" data-testid={`routine-${i}`}>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${r.color}22` }}>
                  <r.icon className="h-6 w-6" style={{ color: r.color }} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[var(--dark)]">{r.type}</h3>
                  <p className="text-sm text-[var(--dark)]/50 mt-1">{r.description}</p>
                </div>
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <ol className="space-y-3">
                  {r.steps.map((step, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-[var(--dark)]/60">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full text-white text-xs flex items-center justify-center font-bold" style={{ background: r.color }}>{j + 1}</span>
                      <span className="pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="bg-[var(--cream)] rounded-xl p-5">
                  <p className="text-sm font-semibold text-[var(--dark)] mb-3">Productos recomendados:</p>
                  {r.recommended.map((prod, k) => (
                    <div key={k} className="flex items-center gap-2 text-sm text-[var(--violet)] mb-2">
                      <ArrowRight className="h-3 w-3" /> {prod}
                    </div>
                  ))}
                  <Link to="/tienda" className="mt-4 block">
                    <Button size="sm" className="bg-[var(--violet)] text-white rounded-full text-xs px-5">
                      Ver productos
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
